import Papa from 'papaparse';

/**
 * Reading a supplied benchmark file in the browser, and checking it before it
 * is sent.
 *
 * The checks mirror the loader's own refusals so a user hears about a bad file
 * straight away rather than from a queued run that stops. The server stays
 * authoritative: anything this lets through, the loader still checks.
 *
 * Header matching, the canonical names and the aliases follow the
 * orchestration service's app/utilities/field_mapping.py, so a header this
 * recognises is one the loader will recognise too.
 */

export type SampleKind = 'alerts' | 'kyc';

/**
 * Inline records travel in the request and then in the flow's queued state for
 * the whole run, so larger samples need a file reference, which is not
 * offered yet.
 */
export const INLINE_MAX_ROWS = 2000;
export const INLINE_MAX_BYTES = 5 * 1024 * 1024;

/** The loader template's max_alerts; above this the sample is cut down. */
export const TEMPLATE_MAX_ALERTS = 300;

export const ACCEPTED_EXTENSIONS = ['.csv', '.json', '.jsonl'];

export interface ParsedSample {
    kind: SampleKind;
    fileName: string;
    bytes: number;
    rows: Record<string, unknown>[];
    /** Every column seen, in first-seen order. */
    headers: string[];
    /** Rows the CSV parser could not split cleanly, reported rather than hidden. */
    parseWarnings: string[];
}

export interface SampleCheck {
    /** The file cannot be submitted until these are fixed. */
    blocks: string[];
    /** It can be submitted, but these will cost something. */
    warnings: string[];
}

export class SampleParseError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'SampleParseError';
    }
}

/* ------------------------------------------------------------------ *
 * Headers
 * ------------------------------------------------------------------ */

// Any letter or digit in any script, as Python's str.isalnum() reads it. Built
// at runtime because the compile target predates Unicode property escapes.
const ALPHANUMERIC = new RegExp('[\\p{L}\\p{N}]', 'u');

/** Lowercase, non-alphanumerics to single underscores: `Case ID` = `case_id` = `CASE-ID`. */
export function normalizeHeader(value: unknown): string {
    if (value === null || value === undefined) return '';
    return Array.from(String(value).trim())
        .map((ch) => (ALPHANUMERIC.test(ch) ? ch.toLowerCase() : '_'))
        .join('')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
}

const ALERT_CANONICAL = [
    'alert_ref', 'customer_ref', 'scenario_id', 'rule_name', 'alert_score',
    'score_breakdown', 'trigger_features', 'context_features',
    'priority', 'priority_source', 'disposition',
    'reason_code', 'sar_filed', 'opened_at', 'closed_at', 'config_epoch'
];

const ALERT_ALIASES: Record<string, string> = {
    case_id: 'alert_ref', alert_id: 'alert_ref', alertid: 'alert_ref', reference: 'alert_ref',
    customer_id: 'customer_ref', party_id: 'customer_ref',
    scenario: 'scenario_id', scenario_code: 'scenario_id', rule_id: 'scenario_id',
    rule: 'rule_name', rule_description: 'rule_name', scenario_name: 'rule_name',
    score: 'alert_score', risk_score: 'alert_score',
    alert_date: 'opened_at', created_date: 'opened_at', opened_date: 'opened_at',
    closed_date: 'closed_at', closure_date: 'closed_at',
    closure_reason: 'disposition', outcome: 'disposition', resolution: 'disposition', final_disposition: 'disposition',
    priority_tier: 'priority', tier: 'priority',
    sar: 'sar_filed', str_filed: 'sar_filed', sar_raised: 'sar_filed',
    alert_context: 'context_features', context: 'context_features', context_attributes: 'context_features',
    supporting_features: 'context_features', alert_attributes: 'context_features'
};

/** Columns that take many sources by design, so two landing on one is not a conflict. */
const PACKED_ALERT_COLUMNS = new Set(['score_breakdown', 'trigger_features', 'context_features']);

const KYC_CANONICAL = [
    'customer_id', 'account_type', 'customer_type', 'customer_segment',
    'customer_acct_age_days', 'onboarding_date', 'date_of_incorporation',
    'legal_name', 'jurisdiction', 'country_of_incorporation', 'country_of_operations',
    'customer_risk_rating', 'kyc_tier',
    'pep_flag', 'sanctions_flag', 'adverse_media_flag', 'debarment_flag',
    'kyc_as_of'
];

const KYC_ALIASES: Record<string, string> = {
    customer_ref: 'customer_id', cust_id: 'customer_id', party_id: 'customer_id',
    client_id: 'customer_id', customer_number: 'customer_id',
    customer_name: 'legal_name', name: 'legal_name', entity_name: 'legal_name',
    company_name: 'legal_name', full_name: 'legal_name',
    account_open_date: 'onboarding_date', open_date: 'onboarding_date',
    onboarded_on: 'onboarding_date', relationship_start_date: 'onboarding_date',
    incorporation_date: 'date_of_incorporation',
    segment: 'customer_segment', industry: 'customer_segment',
    risk_rating: 'customer_risk_rating', risk_level: 'customer_risk_rating', crr: 'customer_risk_rating',
    pep: 'pep_flag', is_pep: 'pep_flag',
    sanctions: 'sanctions_flag', sanctioned: 'sanctions_flag',
    adverse_media: 'adverse_media_flag', debarred: 'debarment_flag',
    as_of: 'kyc_as_of', as_of_date: 'kyc_as_of', kyc_as_of_date: 'kyc_as_of',
    snapshot_date: 'kyc_as_of', effective_date: 'kyc_as_of', valid_from: 'kyc_as_of'
};

export interface HeaderResolution {
    /** Canonical field → the supplied columns that resolve to it. */
    byField: Map<string, string[]>;
    /** Supplied column → the canonical field it resolves to. */
    fieldOf: Map<string, string>;
    /** Columns no name or alias recognises; the server may still identify them. */
    unrecognised: string[];
}

export function resolveHeaders(headers: string[], kind: SampleKind): HeaderResolution {
    const canonical = new Set(kind === 'alerts' ? ALERT_CANONICAL : KYC_CANONICAL);
    const aliases = kind === 'alerts' ? ALERT_ALIASES : KYC_ALIASES;
    const byField = new Map<string, string[]>();
    const fieldOf = new Map<string, string>();
    const unrecognised: string[] = [];

    for (const header of headers) {
        const norm = normalizeHeader(header);
        const field = canonical.has(norm) ? norm : aliases[norm];
        if (!field) {
            unrecognised.push(header);
            continue;
        }
        fieldOf.set(header, field);
        byField.set(field, [...(byField.get(field) ?? []), header]);
    }
    return { byField, fieldOf, unrecognised };
}

/* ------------------------------------------------------------------ *
 * Parsing
 * ------------------------------------------------------------------ */

function extensionOf(fileName: string): string {
    const match = /\.[^.]+$/.exec(fileName.toLowerCase());
    return match ? match[0] : '';
}

function headersOf(rows: Record<string, unknown>[]): string[] {
    const seen = new Set<string>();
    const headers: string[] = [];
    for (const row of rows) {
        for (const key of Object.keys(row)) {
            if (!seen.has(key)) {
                seen.add(key);
                headers.push(key);
            }
        }
    }
    return headers;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

function shapeError(kind: SampleKind): SampleParseError {
    return kind === 'alerts'
        ? new SampleParseError("A JSON sample must be a list of alert objects, or an object with an 'alerts' list.")
        : new SampleParseError("A JSON KYC file must be a list of customer objects, or an object with a 'customers' list.");
}

/**
 * Rows from a file's text, left as the file wrote them: the loader converts
 * flags, numbers, dates and JSON-object feature cells itself.
 */
export function parseSampleText(text: string, fileName: string, kind: SampleKind, bytes = text.length): ParsedSample {
    const ext = extensionOf(fileName);
    if (ext === '.xlsx' || ext === '.xls') {
        throw new SampleParseError('Excel files are not accepted. Save the sheet as CSV and upload that.');
    }
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
        throw new SampleParseError('The file must be CSV, JSON or JSONL.');
    }
    if (!text.trim()) throw new SampleParseError('The file is empty.');

    let rows: Record<string, unknown>[] = [];
    const parseWarnings: string[] = [];

    if (ext === '.csv') {
        const result = Papa.parse<Record<string, unknown>>(text, { header: true, skipEmptyLines: 'greedy' });
        rows = result.data.filter(isPlainObject);
        const rowErrors = result.errors.filter((e) => e.row !== undefined);
        if (rowErrors.length) {
            const first = rowErrors[0];
            parseWarnings.push(
                `${rowErrors.length} ${rowErrors.length === 1 ? 'row does' : 'rows do'} not split cleanly into the header's columns ` +
                `(first at row ${(first.row ?? 0) + 2}: ${first.message}). Check for unquoted commas or line breaks.`
            );
        }
    } else if (ext === '.json') {
        let parsed: unknown;
        try {
            parsed = JSON.parse(text);
        } catch {
            throw new SampleParseError('The file is not valid JSON.');
        }
        // The same wrappers the loader accepts.
        const wrappers = kind === 'alerts' ? ['alerts', 'records'] : ['customers', 'records'];
        const container = isPlainObject(parsed) ? parsed : null;
        const wrapped = container ? wrappers.map((key) => container[key]).find(Array.isArray) : undefined;
        const list = Array.isArray(parsed) ? parsed : (wrapped as unknown[] | undefined) ?? null;
        if (!list || !list.every(isPlainObject)) throw shapeError(kind);
        rows = list as Record<string, unknown>[];
    } else {
        const lines = text.split(/\r?\n/).filter((line) => line.trim());
        rows = lines.map((line, index) => {
            let parsed: unknown;
            try {
                parsed = JSON.parse(line);
            } catch {
                throw new SampleParseError(`Line ${index + 1} is not valid JSON.`);
            }
            if (!isPlainObject(parsed)) throw new SampleParseError(`Line ${index + 1} is not an object.`);
            return parsed;
        });
    }

    if (!rows.length) throw new SampleParseError('The file has no records.');

    return { kind, fileName, bytes, rows, headers: headersOf(rows), parseWarnings };
}

export async function parseSampleFile(file: File, kind: SampleKind): Promise<ParsedSample> {
    return parseSampleText(await file.text(), file.name, kind, file.size);
}

/* ------------------------------------------------------------------ *
 * Checks
 * ------------------------------------------------------------------ */

const blank = (value: unknown) => value === null || value === undefined || String(value).trim() === '';

/** The value of the first column that resolves to a field. */
function valueOf(row: Record<string, unknown>, columns: string[] | undefined): unknown {
    if (!columns) return undefined;
    for (const column of columns) {
        if (!blank(row[column])) return row[column];
    }
    return columns.length ? row[columns[0]] : undefined;
}

function duplicates(values: string[]): string[] {
    const seen = new Set<string>();
    const repeated = new Set<string>();
    for (const value of values) {
        if (seen.has(value)) repeated.add(value);
        seen.add(value);
    }
    return [...repeated];
}

function listSome(values: string[], limit = 3): string {
    const shown = values.slice(0, limit).join(', ');
    return values.length > limit ? `${shown} and ${values.length - limit} more` : shown;
}

/** What each optional field costs when it is missing. */
const MISSING_FIELD_COST: [string, string][] = [
    ['customer_ref', 'No customer column: only alert-level scoring is possible, with no KYC or context layers.'],
    ['disposition', 'No outcome column: ranking quality, hit rates and lift cannot be measured.'],
    ['priority', 'No tier column: the comparison with your current tiers cannot be made.'],
    ['opened_at', 'No alert date: account tenure is computed as of today rather than as of each alert.']
];

export function checkAlertSample(sample: ParsedSample): SampleCheck {
    const blocks: string[] = [];
    const warnings: string[] = [...sample.parseWarnings];
    const { byField, unrecognised } = resolveHeaders(sample.headers, 'alerts');

    if (sample.rows.length > INLINE_MAX_ROWS || sample.bytes > INLINE_MAX_BYTES) {
        blocks.push(
            `The file has ${sample.rows.length.toLocaleString()} alerts (${(sample.bytes / 1024 / 1024).toFixed(1)} MB). ` +
            `Samples of up to ${INLINE_MAX_ROWS.toLocaleString()} alerts and 5 MB can be benchmarked from here; larger ones are not supported yet.`
        );
    }

    for (const [field, columns] of byField) {
        if (columns.length > 1 && !PACKED_ALERT_COLUMNS.has(field)) {
            blocks.push(`More than one column means ${field}: ${columns.join(' and ')}. Keep one and remove the other.`);
        }
    }

    const refColumns = byField.get('alert_ref');
    const scenarioColumns = byField.get('scenario_id');
    if (!refColumns) {
        warnings.push("No column is recognised as the alert reference (alert_ref). The server's model may still identify one; otherwise rename the column.");
    }
    if (!scenarioColumns) {
        warnings.push("No column is recognised as the scenario (scenario_id). The server's model may still identify one; otherwise rename the column.");
    }

    if (refColumns || scenarioColumns) {
        const blankRows = sample.rows.filter((row) =>
            (refColumns && blank(valueOf(row, refColumns))) || (scenarioColumns && blank(valueOf(row, scenarioColumns)))
        ).length;
        if (blankRows) {
            blocks.push(`${blankRows} of ${sample.rows.length} alerts have an empty alert reference or scenario. Fill them in or remove those rows.`);
        }
    }

    if (refColumns) {
        const refs = sample.rows.map((row) => valueOf(row, refColumns)).filter((v) => !blank(v)).map((v) => String(v).trim());
        const repeated = duplicates(refs);
        if (repeated.length) {
            blocks.push(`Each alert must appear once; ${repeated.length} ${repeated.length === 1 ? 'reference repeats' : 'references repeat'} (${listSome(repeated)}).`);
        }
    }

    for (const [field, cost] of MISSING_FIELD_COST) {
        if (!byField.has(field)) warnings.push(cost);
    }

    if (sample.rows.length > TEMPLATE_MAX_ALERTS && sample.rows.length <= INLINE_MAX_ROWS) {
        warnings.push(
            `The file has ${sample.rows.length.toLocaleString()} alerts; up to ${TEMPLATE_MAX_ALERTS} are scored. ` +
            'The sample is cut down keeping its mix of scenarios, periods and outcomes, rather than scored whole.'
        );
    }

    if (unrecognised.length) {
        warnings.push(
            `${unrecognised.length} ${unrecognised.length === 1 ? 'column is' : 'columns are'} not a known name (${listSome(unrecognised)}). ` +
            'The server may identify them; a column it maps to a required field must be confirmed by a person before the run.'
        );
    }

    return { blocks, warnings };
}

/** The customer references the alerts point at, for matching KYC against. */
export function alertCustomerRefs(alerts: ParsedSample): Set<string> {
    const { byField } = resolveHeaders(alerts.headers, 'alerts');
    const columns = byField.get('customer_ref');
    const refs = new Set<string>();
    if (!columns) return refs;
    for (const row of alerts.rows) {
        const value = valueOf(row, columns);
        if (!blank(value)) refs.add(String(value).trim());
    }
    return refs;
}

export function checkKycSample(kyc: ParsedSample, alerts: ParsedSample | null): SampleCheck {
    const blocks: string[] = [];
    const warnings: string[] = [...kyc.parseWarnings];
    const { byField } = resolveHeaders(kyc.headers, 'kyc');

    if (kyc.rows.length > INLINE_MAX_ROWS || kyc.bytes > INLINE_MAX_BYTES) {
        blocks.push(`The KYC file is larger than ${INLINE_MAX_ROWS.toLocaleString()} records or 5 MB, which cannot be sent from here yet.`);
    }

    const idColumns = byField.get('customer_id');
    if (!idColumns) {
        warnings.push(
            'No column is recognised as the customer identifier (customer_id or customer_ref). ' +
            'It is never taken on the model\'s word alone, so the run will be refused unless a person has confirmed a mapping for it.'
        );
    } else {
        if (idColumns.length > 1) {
            blocks.push(`More than one KYC column means customer_id: ${idColumns.join(' and ')}. Keep one and remove the other.`);
        }
        const ids = kyc.rows.map((row) => valueOf(row, idColumns)).filter((v) => !blank(v)).map((v) => String(v).trim());
        const repeated = duplicates(ids);
        if (repeated.length) {
            blocks.push(`Each customer must appear once in the KYC file; ${repeated.length} ${repeated.length === 1 ? 'customer repeats' : 'customers repeat'} (${listSome(repeated)}).`);
        }

        if (alerts) {
            const wanted = alertCustomerRefs(alerts);
            if (wanted.size) {
                const covered = new Set(ids.filter((id) => wanted.has(id)));
                if (covered.size === 0) {
                    blocks.push("None of the KYC file's customers appear in the alerts. It must be keyed by the same identifier as the alerts' customer column.");
                } else if (covered.size < wanted.size) {
                    warnings.push(
                        `The KYC file covers ${covered.size} of the ${wanted.size} customers in the alerts; ` +
                        'the rest are read from your customer records, which may include changes made after the alert.'
                    );
                }
            }
        }
    }

    if (!byField.has('kyc_as_of')) {
        warnings.push(
            'No as-of date (kyc_as_of). Whether each record predates its alerts is unknown, so the KYC stays qualified for look-ahead.'
        );
    }

    return { blocks, warnings };
}
